import { useMemo } from 'react'
import { useChainId } from 'wagmi'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'next/navigation'
import { API_INDEXER_URL } from '@/config/consts'
import { useAccount } from '@/domain/hooks/useAccount'
import { BaseUnitNumber, NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { useTokenInfo } from './useTokenInfo'
import { usePrivy } from '@privy-io/react-auth'
import { useEulerActivity } from '@/domain/euler/activity'
import type { EulerUserTransaction } from '@/domain/euler/activity'
import { usePricesFromIndexer, type PricesMap } from '@/domain/euler/oracle/usePricesFromIndexer'
import { E_LEND_CHAIN_ID } from '@/config/euler-contracts'

export interface ActivityStats {
  totalEarningsUSD: NormalizedUnitNumber
  totalVolumeUSD: NormalizedUnitNumber
  totalLiquidationUSD: NormalizedUnitNumber
}

export enum TransactionAction {
  SUPPLY = 'supply',
  WITHDRAW = 'withdraw',
  BORROW = 'borrow',
  REPAY = 'repay',
  LIQUIDATION = 'liquidation',
  DEPOSIT = 'deposit',
  DEPOSIT_COLLATERAL = 'depositCollateral',
  WITHDRAW_COLLATERAL = 'withdrawCollateral',
  WITHDRAW_INITIATED = 'withdrawInitiated',
  SWAP = 'swap',
}

export function getActionDisplayName(action: TransactionAction): string {
  const displayNames: Record<TransactionAction, string> = {
    [TransactionAction.SUPPLY]: 'Deposited',
    [TransactionAction.WITHDRAW]: 'Withdrew',
    [TransactionAction.BORROW]: 'Borrowed',
    [TransactionAction.REPAY]: 'Repaid',
    [TransactionAction.LIQUIDATION]: 'Liquidated 😿',
    [TransactionAction.DEPOSIT]: 'Deposited',
    [TransactionAction.DEPOSIT_COLLATERAL]: 'Deposited collateral',
    [TransactionAction.WITHDRAW_COLLATERAL]: 'Withdrew collateral',
    [TransactionAction.WITHDRAW_INITIATED]: 'Withdraw initiated',
    [TransactionAction.SWAP]: 'Swapped',
  }

  return displayNames[action] || action
}

export interface APIActivity {
  id: string
  type: 'pooled' | 'isolated' | 'vault' | 'swap' | 'euler'
  action: TransactionAction
  amount?: string
  collateralAmount?: string
  assets: {
    asset: string
    collateral?: string
    pairAddress?: string
  }
  timestamp: string
  transactionHash: string
  chainId: number
}

export interface ProcessedActivity extends APIActivity {
  assetToken?: Token
  collateralToken?: Token
  formattedDate: string
  /** Euler asset symbol displayed in Market column, e.g. "USDH" */
  vaultName?: string
  /** Euler vault address — used for navigation link */
  vaultAddress?: string
  /** Euler product label, e.g. "HypurrFi Prime" */
  product?: string
}

function calculateActivityStats(activities: ProcessedActivity[]): ActivityStats {
  let totalVolumeUSD = NormalizedUnitNumber(0)
  const totalEarningsUSD = NormalizedUnitNumber(0)
  let totalLiquidationUSD = NormalizedUnitNumber(0)

  for (const activity of activities) {
    const amountUSD =
      activity.assetToken?.toUSD(activity.assetToken?.fromBaseUnit(BaseUnitNumber(activity.amount ?? '0'))) ||
      NormalizedUnitNumber(0)

    const amountCollateralUSD = activity.collateralToken
      ? activity.collateralToken?.toUSD(
          activity.collateralToken?.fromBaseUnit(BaseUnitNumber(activity.collateralAmount ?? 0)),
        )
      : NormalizedUnitNumber(0)

    switch (activity.action) {
      case TransactionAction.LIQUIDATION:
        // Euler liquidations only have the repay (debt) amount — no collateral info.
        // Use amountUSD for Euler; use collateral USD for Hypurr (existing behaviour).
        if (activity.type === 'euler') {
          totalLiquidationUSD = NormalizedUnitNumber(totalLiquidationUSD.plus(amountUSD))
        } else {
          totalLiquidationUSD = NormalizedUnitNumber(totalLiquidationUSD.plus(amountCollateralUSD))
        }
        break
      case TransactionAction.DEPOSIT_COLLATERAL:
      case TransactionAction.WITHDRAW_COLLATERAL:
        totalVolumeUSD = NormalizedUnitNumber(totalVolumeUSD.plus(amountCollateralUSD))
        break
      default:
        totalVolumeUSD = NormalizedUnitNumber(totalVolumeUSD.plus(amountUSD))
        break
    }
  }

  return {
    totalEarningsUSD,
    totalVolumeUSD,
    totalLiquidationUSD,
  }
}

// ---------------------------------------------------------------------------
// Map EulerUserTransaction → APIActivity + pre-build a Token for it
// ---------------------------------------------------------------------------

function mapEulerKindToAction(kind: EulerUserTransaction['kind']): TransactionAction {
  switch (kind) {
    case 'supply':
      return TransactionAction.SUPPLY
    case 'borrow':
      return TransactionAction.BORROW
    case 'repay':
      return TransactionAction.REPAY
    case 'liquidation':
      return TransactionAction.LIQUIDATION
    case 'deposit':
      return TransactionAction.DEPOSIT
    case 'withdrawal':
      return TransactionAction.WITHDRAW
  }
}

function eulerTxToAPIActivity(tx: EulerUserTransaction, chainId: number): APIActivity {
  // The timestamp from the dashboard is unix seconds as a string
  const timestampMs = Number(tx.timestamp) * 1000
  const isoTimestamp = new Date(timestampMs).toISOString()

  return {
    id: `euler-${tx.id}`,
    type: 'euler',
    action: mapEulerKindToAction(tx.kind),
    amount: tx.assets,
    collateralAmount: undefined,
    assets: {
      asset: tx.vault, // vault address — used for token lookup
    },
    timestamp: isoTimestamp,
    transactionHash: tx.txHash,
    chainId,
  }
}

/**
 * Look up the USD price for a given symbol from the Euler Indexer prices map.
 * Falls back to 0 when the map is unavailable or symbol is not found.
 */
function getPriceBySymbol(pricesMap: PricesMap | undefined, symbol: string): string {
  if (!pricesMap) return '0'
  const symbolLower = symbol.toLowerCase()
  for (const priceData of pricesMap.values()) {
    if (priceData.symbol.toLowerCase() === symbolLower) {
      return String(priceData.price)
    }
  }
  return '0'
}

/**
 * Build a minimal Token from the dashboard's pre-enriched metadata.
 * Uses the Euler Indexer prices to attach a real USD price when available.
 */
function buildTokenFromEulerTx(tx: EulerUserTransaction, pricesMap: PricesMap | undefined): Token {
  return new Token({
    name: tx.assetSymbol,
    address: CheckedAddress(tx.vault),
    decimals: tx.assetDecimals,
    symbol: TokenSymbol(tx.assetSymbol),
    unitPriceUsd: getPriceBySymbol(pricesMap, tx.assetSymbol),
  })
}

// ---------------------------------------------------------------------------
// Main hook
// ---------------------------------------------------------------------------

export function useActivity(): {
  activities: ProcessedActivity[]
  activityStats: ActivityStats
  isLoading: boolean
  guestMode: boolean
  openConnectModal: () => void
} {
  const chainId = useChainId()
  const searchParams = useSearchParams()
  const urlAddress = searchParams.get('address')
  const account = useAccount()

  // Use URL address if provided, otherwise use connected account
  const effectiveAddress = urlAddress || account
  const guestMode = !effectiveAddress
  const { connectWallet } = usePrivy()

  // ---- Hypurr indexer data (existing) ----
  const { data: activitiesData, isLoading: isLoadingActivities } = useQuery({
    queryKey: ['activity', chainId, effectiveAddress],
    queryFn: async (): Promise<APIActivity[]> => {
      if (!effectiveAddress || !chainId) return []

      const response = await fetch(`${API_INDEXER_URL}/api/chains/${chainId}/users/${effectiveAddress}/history`)

      if (!response.ok) {
        throw new Error('Failed to fetch activity data')
      }
      const data = await response.json()

      if (Array.isArray(data)) {
        return data
      }

      if (data && Array.isArray(data.activities)) {
        return data.activities
      }

      if (data && Array.isArray(data.data)) {
        return data.data
      }

      // If we can't find an array, return empty array
      console.warn('API response is not in expected format:', data)
      return []
    },
    enabled: !!effectiveAddress && !!chainId,
    refetchOnMount: false,
  })

  // ---- Euler prices (for USD calculations) ----
  // Always fetch from HyperEVM (999) since Euler activities are HyperEVM-only
  const { data: eulerPricesMap, isLoading: isLoadingPrices } = usePricesFromIndexer(E_LEND_CHAIN_ID)

  // ---- Euler dashboard data (new) ----
  const { transactions: eulerTransactions, isLoading: isLoadingEuler } = useEulerActivity()

  // Convert Euler transactions to APIActivity format & build token + metadata maps
  const { eulerActivities, eulerTokenMap, eulerMetadataMap } = useMemo(() => {
    if (!eulerTransactions.length)
      return {
        eulerActivities: [] as APIActivity[],
        eulerTokenMap: new Map<string, Token>(),
        eulerMetadataMap: new Map<string, { vaultName: string; vaultAddress: string; product: string | null }>(),
      }

    const activities: APIActivity[] = []
    const tokenMap = new Map<string, Token>()
    const metadataMap = new Map<string, { vaultName: string; vaultAddress: string; product: string | null }>()

    for (const tx of eulerTransactions) {
      const activity = eulerTxToAPIActivity(tx, E_LEND_CHAIN_ID)
      activities.push(activity)

      // Build a token keyed by vault address (lowercase) for lookup
      const key = tx.vault.toLowerCase()
      if (!tokenMap.has(key)) {
        tokenMap.set(key, buildTokenFromEulerTx(tx, eulerPricesMap))
      }

      // Store vault metadata keyed by activity id
      metadataMap.set(activity.id, {
        vaultName: tx.assetSymbol,
        vaultAddress: tx.vault,
        product: tx.product,
      })
    }

    return { eulerActivities: activities, eulerTokenMap: tokenMap, eulerMetadataMap: metadataMap }
  }, [eulerTransactions, eulerPricesMap])

  // ---- Merge both sources ----
  const mergedActivitiesData: APIActivity[] = useMemo(() => {
    const hypurr = Array.isArray(activitiesData) ? activitiesData : []
    const all = [...hypurr, ...eulerActivities]

    // Deduplicate by id only. We intentionally do NOT dedup by transactionHash
    // because EVC-batched Euler transactions pack multiple operations (e.g.
    // supply + borrow) into a single tx, producing distinct activities that
    // share the same txHash but have different ids.
    // Cross-source collisions won't happen: Hypurr IDs are API-assigned,
    // Euler IDs are prefixed with "euler-".
    const seenId = new Set<string>()
    const deduped: APIActivity[] = []
    for (const a of all) {
      const key = a.id
      if (key && seenId.has(key)) continue
      if (key) seenId.add(key)
      deduped.push(a)
    }

    // Sort newest first
    deduped.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())

    return deduped
  }, [activitiesData, eulerActivities])

  // ---- Token enrichment (only for Hypurr activities) ----
  // Euler activities already have token metadata from the dashboard
  const hypurrOnly = useMemo(
    () => (Array.isArray(activitiesData) ? activitiesData : []).filter((a) => a.type !== 'euler'),
    [activitiesData],
  )
  const { tokens, isLoading: isLoadingTokens } = useTokenInfo({ activitiesData: hypurrOnly })

  // In guest mode, return empty data
  if (guestMode) {
    return {
      activities: [],
      activityStats: {
        totalEarningsUSD: NormalizedUnitNumber(0),
        totalVolumeUSD: NormalizedUnitNumber(0),
        totalLiquidationUSD: NormalizedUnitNumber(0),
      },
      isLoading: false,
      guestMode: true,
      openConnectModal: connectWallet,
    }
  }

  const processedActivities: ProcessedActivity[] = mergedActivitiesData.map((activity) => {
    let assetToken: Token | undefined
    let collateralToken: Token | undefined

    if (activity.type === 'euler') {
      // For Euler activities, look up from the pre-built token map
      const key = activity.assets?.asset?.toLowerCase()
      if (key) {
        assetToken = eulerTokenMap.get(key)
      }
    } else {
      // For Hypurr activities, look up from the enriched tokens list
      if (activity.assets?.asset) {
        assetToken = tokens.find((token) => token.address.toLowerCase() === activity.assets?.asset.toLowerCase())
      }

      if (activity.assets?.collateral) {
        collateralToken = tokens.find(
          (token) => token.address.toLowerCase() === activity.assets?.collateral?.toLowerCase(),
        )
      }
    }

    // Format date
    const formattedDate = new Date(activity.timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })

    // Attach Euler vault metadata if available
    const eulerMeta = activity.type === 'euler' ? eulerMetadataMap.get(activity.id) : undefined

    return {
      ...activity,
      assetToken,
      collateralToken,
      formattedDate,
      vaultName: eulerMeta?.vaultName,
      vaultAddress: eulerMeta?.vaultAddress,
      product: eulerMeta?.product ?? undefined,
    }
  })

  const activityStats = calculateActivityStats(processedActivities)

  return {
    activities: processedActivities,
    activityStats,
    isLoading: isLoadingActivities || isLoadingTokens || isLoadingEuler || isLoadingPrices,
    guestMode: false,
    openConnectModal: connectWallet,
  }
}
